/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage, useTranslations } from "@i18n/react";
import {
  HallowAnalyticsEvent,
  useAnalytics,
  useViewedScreen,
} from "@packages/analytics";
import type { ThemeType } from "@packages/ui";
import {
  Button,
  CloseIcon,
  createModal,
  IconButton,
  Text,
  useModal,
} from "@packages/ui";
import * as RadixUIAlertDialog from "@radix-ui/react-alert-dialog";
import { AnimatePresence, motion } from "motion/react";
import { Fragment, useCallback } from "react";
import { useMediaQuery } from "usehooks-ts";

import { useModalResolution } from "../hooks/useModalResolution";
import { Durations } from "../lib/enums/durations";
import { ScreenNames } from "../lib/enums/screenNames";
import { Screens } from "../lib/enums/screens";
import { ZIndices } from "../lib/enums/zIndices";
import { Overlay } from "./blocks/Overlay";

const cssAlertDialogContent: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.background,
  borderRadius: theme.radius.l,
  bottom: theme.spacing.xl,
  color: theme.semanticColors.primary,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.l,
  left: "50%",
  maxWidth: `min(480px, calc(100dvw - calc(${theme.spacing.l} * 2)))`,
  overflow: "hidden",
  padding: theme.spacing.l,
  position: "fixed",
  textAlign: "center",
  width: "100dvw",
  zIndex: ZIndices.Content,
  [`@media ${Screens.Large}`]: {
    bottom: "auto",
    paddingBottom: theme.spacing.xl,
    paddingTop: theme.spacing.xl,
    textAlign: "left",
    top: "50%",
  },
});
const cssAlertDialogDescription: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigher,
  margin: 0,
  [`@media ${Screens.Large}`]: {
    color: theme.semanticColors.primary,
  },
});
const cssAlertDialogTitle: Interpolation = {
  margin: 0,
};
const cssDivHeader: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing.l,
  justifyContent: "center",
  [`@media ${Screens.Large}`]: {
    justifyContent: "space-between",
  },
});
const cssDivTextContainer: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.xs,
  [`@media ${Screens.Large}`]: {
    gap: theme.spacing.m,
  },
});
const cssIconButton: Interpolation = {
  display: "none",
  [`@media ${Screens.Large}`]: {
    display: "flex",
  },
};

export type ProfileReadyProps = {
  modalName?: string;
};

export const ProfileReady = createModal<ProfileReadyProps>(
  ({ modalName = "kids_profile_created_confirmation" }) => {
    const modal = useModal();
    const { onOpenChange, resolve } = useModalResolution({ modal });

    const { track } = useAnalytics();
    const isScreenLarge = useMediaQuery(Screens.Large);
    const t = useTranslations();
    useViewedScreen({
      event: HallowAnalyticsEvent.ViewedModal,
      isReady: modal.visible,
      properties: {
        modal_name: modalName,
      },
      screen: ScreenNames.ProfileSelector,
    });

    const onClickAction = useCallback(() => {
      track({
        event: HallowAnalyticsEvent.TappedModalPrimaryCTA,
        properties: {
          cta_lokalise_key: "general_word_okay",
          cta_text: t("general_word_okay"),
          modal_name: modalName,
          screen_name: ScreenNames.ProfileSelector,
        },
      });
      resolve(true, () => modal.hide());
    }, [modal.hide, resolve, t, track]);

    return (
      <RadixUIAlertDialog.Root onOpenChange={onOpenChange} open={modal.visible}>
        <RadixUIAlertDialog.Portal>
          <AnimatePresence>
            <RadixUIAlertDialog.Overlay asChild>
              <Overlay onClick={() => onOpenChange(false)} />
            </RadixUIAlertDialog.Overlay>
            <Fragment key="alertDialogContent">
              {modal.visible && (
                <RadixUIAlertDialog.Content
                  asChild
                  onOpenAutoFocus={(event) => {
                    event.preventDefault();
                    document.getElementById("alert-dialog-action")?.focus();
                  }}
                >
                  <motion.div
                    animate={{
                      opacity: 1,
                      scale: 1,
                      x: "-50%",
                      y: isScreenLarge ? "-50%" : 0,
                    }}
                    css={cssAlertDialogContent}
                    exit={{
                      opacity: 0,
                      scale: isScreenLarge ? 0.95 : 1,
                      x: "-50%",
                      y: isScreenLarge ? "-50%" : "100%",
                    }}
                    initial={{
                      opacity: 0,
                      scale: isScreenLarge ? 0.95 : 1,
                      x: "-50%",
                      y: isScreenLarge ? "-50%" : "100%",
                    }}
                    transition={{
                      duration: Durations.Long,
                      type: "spring",
                    }}
                  >
                    <div css={cssDivTextContainer}>
                      <div css={cssDivHeader}>
                        <Text
                          asChild
                          size={isScreenLarge ? "s" : "l"}
                          type={isScreenLarge ? "headline" : "title"}
                        >
                          <RadixUIAlertDialog.Title css={cssAlertDialogTitle}>
                            <I18nMessage message="kids_profile_created_modal_title" />
                          </RadixUIAlertDialog.Title>
                        </Text>
                        <RadixUIAlertDialog.Cancel asChild>
                          <IconButton
                            css={cssIconButton}
                            icon={<CloseIcon size={20} />}
                            size="s"
                            variant="transparentPrimary"
                          />
                        </RadixUIAlertDialog.Cancel>
                      </div>
                      <Text asChild>
                        <RadixUIAlertDialog.Description
                          css={cssAlertDialogDescription}
                        >
                          <I18nMessage message="kids_profile_created_modal_subtitle_web" />
                        </RadixUIAlertDialog.Description>
                      </Text>
                    </div>
                    <RadixUIAlertDialog.Action
                      asChild
                      id="alert-dialog-action"
                      onClick={onClickAction}
                    >
                      <Button isFullWidth size="l">
                        <I18nMessage message="general_word_okay" />
                      </Button>
                    </RadixUIAlertDialog.Action>
                  </motion.div>
                </RadixUIAlertDialog.Content>
              )}
            </Fragment>
          </AnimatePresence>
        </RadixUIAlertDialog.Portal>
      </RadixUIAlertDialog.Root>
    );
  },
);
